import type { SessionSubscription } from "@/lib/auth/sessionTypes";
import {
  getPastDueGraceEndsAt,
  isPastDueWithinGrace,
} from "@/lib/billing/grace";

const SUBSCRIPTION_CACHE_BUFFER_MS = 15 * 60 * 1000;
const SUBSCRIPTION_CACHE_MAX_MS = 6 * 60 * 60 * 1000;

type CachedSubscriptionEntry = {
  expiresAt: number;
  subscription: SessionSubscription;
};

const subscriptionCache = new Map<string, CachedSubscriptionEntry>();

function getSubscriptionExpiryTime(
  subscription: SessionSubscription,
): number | null {
  if (subscription.status === "past_due") {
    return getPastDueGraceEndsAt(subscription);
  }

  const expirySource =
    subscription.status === "trialing"
      ? subscription.trial_end
      : subscription.current_period_end;

  if (!expirySource) return null;

  const expiryTime = new Date(expirySource).getTime();
  return Number.isFinite(expiryTime) ? expiryTime : null;
}

export function isSubscriptionCurrentlyValid(
  subscription: SessionSubscription,
): boolean {
  if (subscription.status === "active") {
    return true;
  }

  if (
    subscription.status === "trialing" &&
    subscription.trial_end &&
    new Date(subscription.trial_end).getTime() > Date.now()
  ) {
    return true;
  }

  return isPastDueWithinGrace(subscription);
}

export function getCachedSubscription(
  orgId: string,
): SessionSubscription | undefined {
  const cached = subscriptionCache.get(orgId);
  if (!cached) return undefined;

  if (cached.expiresAt <= Date.now()) {
    subscriptionCache.delete(orgId);
    return undefined;
  }

  return cached.subscription;
}

export function cacheValidSubscription(
  orgId: string,
  subscription: SessionSubscription | undefined,
): void {
  if (!subscription || !isSubscriptionCurrentlyValid(subscription)) {
    subscriptionCache.delete(orgId);
    return;
  }

  const now = Date.now();
  const expiryTime = getSubscriptionExpiryTime(subscription);
  const maxCacheUntil = now + SUBSCRIPTION_CACHE_MAX_MS;
  const bufferedExpiry =
    expiryTime != null ? expiryTime - SUBSCRIPTION_CACHE_BUFFER_MS : null;
  const cacheUntil =
    bufferedExpiry != null
      ? Math.min(maxCacheUntil, bufferedExpiry)
      : maxCacheUntil;

  if (cacheUntil <= now) {
    subscriptionCache.delete(orgId);
    return;
  }

  subscriptionCache.set(orgId, {
    subscription,
    expiresAt: cacheUntil,
  });
}

export function clearSubscriptionCache() {
  subscriptionCache.clear();
}

export function selectActiveSubscription(
  rows: SessionSubscription[],
): SessionSubscription | undefined {
  return rows.find(
    (subscription) =>
      subscription.status === "active" ||
      subscription.status === "past_due" ||
      (subscription.status === "trialing" &&
        subscription.trial_end &&
        new Date(subscription.trial_end).getTime() > Date.now()),
  );
}
